import { action, flow } from 'mobx';
import { codeSplit, Controller, FetchOptions, FetchPageOptions, StoreContainer } from 'bernie-core';
import { PageData } from 'bernie-http';
import { ClientLogger } from 'bernie-client';
import { AnalyticsStore, ContextStore } from 'bernie-plugin-mobx';
import { getBexApiContext } from 'bernie-context';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import { Localization } from 'bernie-l10n';
import { PageId, getPageName, generateRequestOptions, needSearchFis } from 'utility';
import { getNavigationIdFromQuery } from 'utility/navigation-identity';

import {
  ErrorStore,
  FlightsMerchandisingConfirmationCriteriaStore,
  FlightsMerchandisingConfirmationStore
} from 'stores';

import { FlightsQueryState, FlightsShoppingPwaMerchandisingPagePresentationLoaded } from '__generated__/api';

export class FlightsMerchandisingConfirmationController implements Controller {
  public pageId = PageId.FLIGHTS_MERCHANDISHING_CONFIRMATION;
  public path = '/trips/flight-add-ons-confirmation';
  public routeName = 'flight-add-ons-confirmation';
  public bundles = [];
  public exact = true;
  /* istanbul ignore next */
  public component = codeSplit(
    () => import(/* webpackChunkName: "servicing" */ '../views/flights-merchandising-confirmation')
  );
  public pendingPromises = [];
  private logger = ClientLogger.getLoggerWithIdentifier('controller.flightsMerchandisingConfirmation');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public fetch(options: FetchOptions): Promise<any> {
    if (options.isServer) {
      return this.fetchServerSide(options);
    }
    const searchNeeded = needSearchFis(options, false);
    if (searchNeeded) {
      return this.fetchClientSide(options);
    }

    return Promise.resolve({});
  }

  public fetchPageData(pageData: FetchPageOptions): Promise<PageData> {
    const localizer = new Localization(pageData.context.locale);
    const query = pageData.query;
    const title = localizer.formatText('merchandising.server.page.title');
    const pageName = getPageName(this.pageId);
    const navigationId = getNavigationIdFromQuery(query);

    return Promise.resolve({ title, pageId: this.pageId, pageName, navigationId });
  }

  private async fetchServerSide(options: FetchOptions) {
    const {
      analyticsStore,
      variables,
      flightsMerchandisingConfirmationStore,
      flightsMerchandisingConfirmationCriteriaStore,
      errorStore
    } = this.getFetchVariables(options.stores, options.isServer);
    flightsMerchandisingConfirmationCriteriaStore.setCriteriaFromUrl(options);
    const response = await flightsMerchandisingConfirmationStore.fetchLoading(
      variables,
      generateRequestOptions(options.request, options.isServer, this.pageId),
      analyticsStore
    );

    if (!response) {
      errorStore.setClientFetchFailure(true);

      return;
    }

    flightsMerchandisingConfirmationCriteriaStore.hydrate({ tripId: options.query?.tripId });
    flightsMerchandisingConfirmationStore.hydrate({ pagePresentationLoading: response.pagePresentation });
    errorStore.hydrate({
      serverErrorMessaging: flightsMerchandisingConfirmationStore.pagePresentationLoading?.serverErrorMessaging
    });

    flightsMerchandisingConfirmationStore.loading = true;
    errorStore.noApiResponse = false;
  }

  private fetchClientSide(options: FetchOptions) {
    if (this.pendingPromises.length > 0) {
      this.pendingPromises.forEach(p => p.cancel());
    }

    const clientPromise = this.createFlowForClientFetch(options, this.pageId);

    this.pendingPromises.push(clientPromise);

    return clientPromise;
  }

  @action
  private createFlowForClientFetch = flow(function* (options: FetchOptions, pageId: string) {
    const {
      analyticsStore,
      variables,
      flightsMerchandisingConfirmationStore,
      flightsMerchandisingConfirmationCriteriaStore,
      errorStore
    } = this.getFetchVariables(options.stores, options.isServer);
    const requestOptions = generateRequestOptions(options.request, options.isServer, pageId);
    flightsMerchandisingConfirmationCriteriaStore.setCriteriaFromUrl(options);
    flightsMerchandisingConfirmationStore.loading = true;
    errorStore.clientFetchFailure = ErrorStore.DEFAULTS.clientFetchFailure;

    try {
      const response: FlightsShoppingPwaMerchandisingPagePresentationLoaded.FlightsMerchandising = yield flightsMerchandisingConfirmationStore.fetchResults(
        variables,
        requestOptions,
        analyticsStore
      );

      if (!response || Object.keys(response).length == 0) {
        errorStore.setClientFetchFailure(true);
        this.logger.logEvent(new SystemEvent(SystemEventLevel.CRITICAL, 'MERCHANDISING_FETCH_FAILED'));

        return;
      }

      flightsMerchandisingConfirmationCriteriaStore.hydrate({ tripId: options.query?.tripId });
      flightsMerchandisingConfirmationStore.hydrate({ pagePresentation: response.pagePresentation });
      this.pendingPromises = [];
      flightsMerchandisingConfirmationStore.loading = false;

      return;
    } catch (error) {
      errorStore.setClientFetchFailure(true);
      this.logger.logEvent(
        new SystemEvent(SystemEventLevel.CRITICAL, 'MERCHANDISING_CONFIRMATION_CLIENT_HYDRATION'),
        error
      );
    }
  });

  private getFetchVariables(stores: StoreContainer, isServer: boolean) {
    const flightsMerchandisingConfirmationCriteriaStore = stores.get<FlightsMerchandisingConfirmationCriteriaStore>(
      'flightsMerchandisingConfirmationCriteriaStore'
    );
    const flightsMerchandisingConfirmationStore = stores.get<FlightsMerchandisingConfirmationStore>(
      'flightsMerchandisingConfirmationStore'
    );
    const bernieContext: ContextStore = stores.get<ContextStore>('context');
    const analyticsStore = stores.get<AnalyticsStore>('analytics');
    const context = getBexApiContext(bernieContext);
    const loadSwitch = isServer ? FlightsQueryState.LOADING : FlightsQueryState.LOADED;
    flightsMerchandisingConfirmationCriteriaStore.merchandisingConfirmationCriteria.queryState = loadSwitch;
    const variables = { context, ...flightsMerchandisingConfirmationCriteriaStore.merchandisingConfirmationCriteria };
    const errorStore = stores.get<ErrorStore>('errorStore');

    return {
      analyticsStore,
      variables,
      flightsMerchandisingConfirmationStore,
      flightsMerchandisingConfirmationCriteriaStore,
      errorStore
    };
  }
}
